import { toggleTheme, useTheme } from '../theme.js';

// 04 #95: the two shipped forms of the theme selector.
//
// ThemeToggleRow is the LOCKED menu row (design pinned at the s103 prototype, label
// amended by the owner at s104): the label flips between "Dark Mode" (while light)
// and "Light Mode" (while dark), and the chip is ALWAYS the INVERSE of the active
// mode: a dark chip with a moon while light, a light chip with a sun while dark.
// It renders ABOVE Sign out in the mobile drawer's bottom section and in the desktop
// account menu (AppShell owns the placement + the divider).
//
// ThemeCornerButton is the SIGNED-OUT control (owner, s104): one adaptive circular
// disc, bottom right; the disc rides the edge-channel token (lighter than the page in
// dark, darker in light) and only the ICON swaps: a sun while dark, a moon while light.
// AppShell renders it only when no user is signed in.
//
// Both are pure consumers of the theme sheet; neither hard-codes a themed color.

function MoonIcon({ size = 12 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

function SunIcon({ size = 12 }: { size?: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" />
    </svg>
  );
}

export function ThemeToggleRow({ variant }: { variant: 'menu' | 'drawer' }): JSX.Element {
  const theme = useTheme();
  const dark = theme === 'dark';
  const rowClass =
    variant === 'drawer'
      ? 'w-full flex items-center gap-2.5 px-2 py-2.5 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition text-left'
      : 'w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition text-left';
  return (
    <button type="button" onClick={toggleTheme} className={rowClass} aria-pressed={dark}>
      <span className="flex-1">{dark ? 'Light Mode' : 'Dark Mode'}</span>
      <span
        className={
          'inline-flex items-center rounded-full px-2.5 py-1 transition-colors ' +
          (dark
            ? 'bg-[var(--chip-light-bg)] text-[var(--chip-light-text)]'
            : 'bg-[var(--chip-dark-bg)] text-[var(--chip-dark-text)]')
        }
        aria-hidden="true"
      >
        {dark ? <SunIcon /> : <MoonIcon />}
      </span>
    </button>
  );
}

export function ThemeCornerButton(): JSX.Element {
  const theme = useTheme();
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="fixed bottom-4 right-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-[rgb(var(--edge-rgb)/0.10)] text-[var(--color-text)] shadow-lg transition hover:bg-[rgb(var(--edge-rgb)/0.16)]"
    >
      {dark ? <SunIcon size={17} /> : <MoonIcon size={17} />}
    </button>
  );
}
